import React, { forwardRef, useEffect, useState } from "react";
import classNames from "classnames";
import { Button } from "..";
import { Intent } from "../../common";
import Icon, { IconName } from "../Icon/icon";

export interface InputGroupProps {
  intent?: Intent;
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  fill?: boolean;
  leftElement?: JSX.Element;
  leftIcon?: IconName;
  onChange?: () => void;
  placeholder?: string;
  rightElement?: JSX.Element;
  type?: string;
  value?: string;

  label?: string;
  required?: boolean;
  helperText?: string;
}

const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>(
  (
    {
      intent = "none",
      className,
      defaultValue,
      disabled = false,
      fill,
      leftElement,
      leftIcon,
      onChange,
      placeholder,
      rightElement,
      type = "text",
      value,
      label,
      required,
      helperText,
    },
    ref
  ) => {
    let lockButton;
    let leftElem: HTMLElement | null = null;
    let rightElem: HTMLElement | null = null;

    const _inputOption = {
      type,
      showPassword: false,
      hasLeftElem: leftElement || leftIcon ? true : false,
      hasRightElem: rightElement || type === "password" ? true : false,
      leftPadding: 12,
      rightPadding: 12,
    };

    const [inputOption, setInputOption] = useState(_inputOption);

    const refHanlders = {
      leftElem: (ref: HTMLSpanElement | null) => {
        leftElem = ref;
      },
      rightElem: (ref: HTMLSpanElement | null) => {
        rightElem = ref;
      },
    };

    useEffect(() => {
      // left/right Element에 따른 패딩값 조정
      const updateInputWidth = () => {
        const leftPadding = leftElem?.offsetWidth;
        const rightPadding = rightElem?.offsetWidth;

        leftPadding && setInputOption((prev) => ({ ...prev, leftPadding }));
        rightPadding && setInputOption((prev) => ({ ...prev, rightPadding }));
      };

      updateInputWidth();
    }, []);

    // password 버튼 처리
    if (type == "password") {
      lockButton = (
        <Button
          className="lock-button"
          disabled={disabled}
          icon={inputOption.showPassword ? "VisibilityOff" : "Visibility"}
          minimal={true}
          size={40}
          intent="tertiary"
          onClick={() => {
            setInputOption((prev) => ({
              ...prev,
              showPassword: !prev.showPassword,
            }));
            if (!inputOption.showPassword) {
              setInputOption((prev) => ({ ...prev, type: "text" }));
            } else {
              setInputOption((prev) => ({ ...prev, type: "password" }));
            }
          }}
        />
      );
    }

    // Input 컴포넌트
    const inputComponent = (
      <div
        className={classNames({
          "form-input-container": true,
          [`${intent}`]: intent !== "none",
        })}
      >
        <input
          className={classNames({
            className: className,
            [`input-${intent}`]: intent !== "none",
            "form-input": true,
            lockButton: lockButton,
          })}
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={onChange}
          placeholder={placeholder}
          type={inputOption.type}
          value={value}
          required={required}
          style={{
            paddingLeft: `${inputOption.leftPadding + 8}px`,
            paddingRight: `${inputOption.rightPadding + 4}px`,
          }}
          ref={ref}
        />

        {inputOption.hasLeftElem && (
          <span
            className="left-element"
            ref={refHanlders.leftElem}
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translate(4px, -50%)",
            }}
          >
            {leftElement && leftElement}
            {leftIcon && <Icon className="left-icon" name={leftIcon} />}
          </span>
        )}

        {inputOption.hasRightElem && (
          <span
            className="right-element"
            ref={refHanlders.rightElem}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translate(-4px, -50%)",
            }}
          >
            {rightElement && rightElement}
            {lockButton && lockButton}
          </span>
        )}
      </div>
    );

    // Label 및 helperText 렌더링
    const maybeRenderLabel = (
      input: JSX.Element,
      { label, helperText }: InputGroupProps
    ) => {
      if (label == null && helperText == null) return input;
      return (
        <label className="input-label">
          <span className="label">{label}</span>
          {input}
          <span className="helper-text">{helperText}</span>
        </label>
      );
    };

    return (
      <div
        className={classNames({
          "input-group": true,
          "is-required": required,
        })}
      >
        {maybeRenderLabel(inputComponent, { label, helperText })}
      </div>
    );
  }
);

export default InputGroup;

